import React, { useState } from 'react';
import { observer } from 'src/utils/mobx-react';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from 'src/domains/common/components/table/table';
import { cn } from 'src/domains/common/components/utils';
import { IconButton } from 'src/domains/common/components/button/icon-button';
import { makeAutoObservable } from 'mobx';
import { entryReasons } from './GamprotectTab.state';
import moment from 'moment';
import { MarkerType } from './types';

class TableState {
    public expandedRows: number[] = [];

    public constructor() {
        makeAutoObservable(this);
    }

    public toggleRowExpansion = (markerId: number): void => {
        if (this.expandedRows.includes(markerId)) {
            this.expandedRows = this.expandedRows.filter((id) => id !== markerId);
        } else {
            this.expandedRows.push(markerId);
        }
    };

    public isExpanded = (markerId: number): boolean => {
        return this.expandedRows.includes(markerId);
    };
}

const convertEntryReasonToDescription = (entryReason: string): string => {
    const foundDescription = entryReasons[entryReason];
    if (foundDescription === undefined) {
        return entryReason;
    }

    return foundDescription;
};

interface ReasonFormPropsType {
    markers: Array<MarkerType>;
}

export const GamprotectMarketsTable = observer('GamprotectMarketsTable', ({ markers }: ReasonFormPropsType) => {
    const [state] = useState(() => new TableState());

    return (
        <div className='relative w-full overflow-auto border border-solid rounded-md border-border'>
            <Table className='table-fixed'>
                <TableHeader className='sticky top-0 bg-white z-[1] shadow-b-shadow'>
                    <TableRow className='!border-b-0'>
                        <TableHead className='!px-2 h-14 w-12'>&nbsp;</TableHead>
                        <TableHead className='py-0 h-14'>
                            <div className='cursor-pointer flex items-center gap-2.5'>ID</div>
                        </TableHead>
                        <TableHead className='py-0 h-14'>
                            <div className='cursor-pointer flex items-center gap-2.5'>Marker</div>
                        </TableHead>
                        <TableHead className='py-0 h-14'>
                            <div className='cursor-pointer flex items-center gap-2.5'>Brand</div>
                        </TableHead>
                        <TableHead className='py-0 h-14'>
                            <div className='cursor-pointer flex items-center gap-2.5'>Created</div>
                        </TableHead>
                        <TableHead className='py-0 h-14'>
                            <div className='cursor-pointer flex items-center gap-2.5'>Ended</div>
                        </TableHead>
                        <TableHead className='py-0 h-14'>
                            <div className='cursor-pointer flex items-center gap-2.5'>Active</div>
                        </TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {markers.map((marker, index) => {
                        const isExpanded = state.isExpanded(marker.id);
                        const rowClass = index % 2 === 0 ? 'bg-white' : 'bg-muted/50';

                        const firstSeen = moment(marker.firstSeen).format('YYYY-MM-DD hh:mm');
                        const lastSeen =
                            marker.lastSeen === undefined ? '-' : moment(marker.lastSeen).format('YYYY-MM-DD hh:mm');

                        return (
                            <React.Fragment key={marker.id}>
                                <TableRow
                                    className={cn(
                                        rowClass,
                                        'data-[state=selected]:bg-muted border-border border-solid border-t-0 border-l-0 border-r-0'
                                    )}
                                    data-test={`marker-row-${marker.id}`}
                                >
                                    <TableCell className='py-0 !px-2 h-14'>
                                        <IconButton
                                            className='no-hover'
                                            variant='ghost'
                                            size='default'
                                            onClick={() => state.toggleRowExpansion(marker.id)}
                                            icon={isExpanded ? 'minus' : 'plus'}
                                            disabled={marker.deletionReason === '' && marker.entryReason === ''}
                                            data-test='expand-button'
                                        />
                                    </TableCell>
                                    <TableCell
                                        className='h-14'
                                        data-test='marker-id'
                                    >
                                        {marker.gamprotectId}
                                    </TableCell>
                                    <TableCell
                                        className='h-14'
                                        data-test='marker-id'
                                    >
                                        {marker.marker}
                                    </TableCell>
                                    <TableCell
                                        className='h-14'
                                        data-test='marker-brand'
                                    >
                                        {marker.brand}
                                    </TableCell>
                                    <TableCell
                                        className='h-14'
                                        data-test='marker-first-seen'
                                    >
                                        {firstSeen}
                                    </TableCell>
                                    <TableCell
                                        className='h-14'
                                        data-test='marker-last-seen'
                                    >
                                        {lastSeen}
                                    </TableCell>
                                    <TableCell
                                        className='h-14'
                                        data-test='marker-active'
                                    >
                                        {marker.active ? 'Yes' : 'No'}
                                    </TableCell>
                                </TableRow>
                                {isExpanded && (
                                    <TableRow
                                        className={cn(
                                            rowClass,
                                            'overflow-hidden border-border border-solid border-b border-t-0 border-l-0 border-r-0'
                                        )}
                                    >
                                        <TableCell
                                            colSpan={6}
                                            className='py-3 last:px-4 first:px-4'
                                        >
                                            <div
                                                className='pl-10 mt-0 mb-3'
                                                data-test='marker-reasons'
                                            >
                                                {marker.entryReason === '' ? null : (
                                                    <p>
                                                        <span className='font-bold'>Entry reason: </span>
                                                        {convertEntryReasonToDescription(marker.entryReason)}
                                                    </p>
                                                )}
                                                {marker.deletionReason === undefined ? null : (
                                                    <p>
                                                        <span className='font-bold'>Deletion reason: </span>
                                                        {marker.deletionReason}
                                                    </p>
                                                )}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </React.Fragment>
                        );
                    })}
                </TableBody>
            </Table>
        </div>
    );
});
